import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./ReferralForm.css";
import { useModalA11y } from "./useModalA11y";
import { EXCLUDED_PRACTICE_ODS } from "../data/excludedPractices";
// Derived from the conversation config (single source of truth) — see
// scripts/gen-options.mjs.
import {
  DISADVANTAGED_GROUPS,
  EMPLOYMENT_OPTIONS,
  PRIOR_PROGRAMMES,
  optionLabel,
} from "../conversation/optionLabels";
import { useI18n } from "../i18n";
import { asset } from "../config";

type GpPractice = { ods_code: string; name: string; postcode: string };

/** Load the baked GP-practice lists (public/data). Type-to-find only — static
 * assets, no runtime NHS API dependency. Fails soft: an empty list just means
 * the field behaves as a plain text input (no one is blocked).
 *
 * Northamptonshire practices are offered ONLY on the WorkWell form: the
 * eligibility engine has ww_eligible = [LLR, NORTHANTS] but c2w_eligible =
 * [LLR], so a Northants resident can never reach a Connect to Work referral. */
function useGpPractices(includeNorthants: boolean): GpPractice[] {
  const [practices, setPractices] = useState<GpPractice[]>([]);
  useEffect(() => {
    let live = true;
    const files = [asset("data/llr_gp_practices.json")];
    if (includeNorthants) files.push(asset("data/northants_gp_practices.json"));
    Promise.all(
      files.map((f) => fetch(f).then((r) => (r.ok ? r.json() : [])).catch(() => [])),
    )
      .then((lists) => {
        if (!live) return;
        const merged = lists
          .flat()
          .filter((p): p is GpPractice => !!p?.ods_code)
          // Practices whose PCN has not signed up to WorkWell. Filtered at
          // load rather than stripped from the baked JSON, so the list stays a
          // faithful copy of the NHS data and the exclusions stay reviewable
          // in one place (with the PCN that caused each one).
          .filter((p) => !EXCLUDED_PRACTICE_ODS.has(p.ods_code));
        merged.sort((a, b) => a.name.localeCompare(b.name));
        setPractices(merged);
      })
      .catch(() => { /* soft-fail: field stays a free-text input */ });
    return () => { live = false; };
  }, [includeNorthants]);
  return practices;
}

type ReferralFormProps = {
  service: string;
  collectedData: Record<string, unknown>;
  /** Called after the form is filled — parent advances to consent #2 (share). */
  onSubmit: () => void;
  onClose: () => void;
};

const str = (v: unknown): string => (v == null ? "" : String(v));

/**
 * Detailed ethnicity categories → the five top-level ONS groups this form
 * offers. Upstream systems (Joy, GP records) use the detailed ONS list — real
 * referrals arrive as "White British", "Any other White background" and so on
 * — and rolling a detailed category up to its parent group is the standard,
 * lossless-in-one-direction mapping, not a guess about what someone meant.
 *
 * Matched on the leading words, so unseen variants within a group still land
 * correctly ("Irish Traveller" → White). Anything unrecognised falls through
 * to empty and the resident chooses.
 */
const ETHNICITY_GROUP_PREFIXES: readonly (readonly [string, string])[] = [
  ["white", "White"],
  ["gypsy", "White"],
  ["irish", "White"],
  ["roma", "White"],
  ["asian", "Asian or Asian British"],
  ["indian", "Asian or Asian British"],
  ["pakistani", "Asian or Asian British"],
  ["bangladeshi", "Asian or Asian British"],
  ["chinese", "Asian or Asian British"],
  ["black", "Black, Black British, Caribbean or African"],
  ["caribbean", "Black, Black British, Caribbean or African"],
  ["african", "Black, Black British, Caribbean or African"],
  ["mixed", "Mixed or Multiple ethnic groups"],
  ["multiple", "Mixed or Multiple ethnic groups"],
  ["arab", "Other ethnic group"],
  ["other", "Other ethnic group"],
  ["prefer", "Prefer not to say"],
];

/**
 * Gender as upstream systems write it → our option list. Joy's example payload
 * carried both "F" and "female" for the same person, so the single-letter
 * forms are mapped explicitly; everything else falls through to the exact
 * (case-insensitive) match, then to empty.
 */
const GENDER_ALIASES: Readonly<Record<string, string>> = {
  f: "Female",
  m: "Male",
  woman: "Female",
  man: "Male",
  nonbinary: "Non-binary",
  "non binary": "Non-binary",
  nb: "Non-binary",
};

const matchGender = (v: unknown): string => {
  const raw = str(v).trim().toLowerCase().replace(/[_-]/g, " ");
  if (!raw) return "";
  const alias = GENDER_ALIASES[raw];
  if (alias) return alias;
  return GENDERS.find((o) => o.toLowerCase() === raw) ?? "";
};

/** Roll a detailed ethnicity up to one of our five groups; "" when unknown. */
const matchEthnicity = (v: unknown): string => {
  const raw = str(v).trim().toLowerCase().replace(/_/g, " ");
  if (!raw) return "";
  const exact = ETHNICITIES.find((o) => o.toLowerCase() === raw);
  if (exact) return exact;
  // Scan the whole phrase, not just the first word: real values lead with a
  // qualifier ("Any other White background" is a WHITE background, not the
  // "Other ethnic group"). So the specific group words are tested first and
  // the generic "other"/"any" catch-all only applies when none of them appear.
  const words = raw.split(/[\s,/-]+/);
  for (const [prefix, group] of ETHNICITY_GROUP_PREFIXES) {
    if (prefix === "other") continue; // generic — considered last
    if (words.some((w) => w.startsWith(prefix))) return group;
  }
  return words.some((w) => w.startsWith("other")) ? "Other ethnic group" : "";
};

/**
 * Match a value from an upstream system against one of our fixed options.
 *
 * The GP pathway seeds gender/ethnicity/education from the Joy payload, where
 * the vocabulary is the referrer's, not ours ("female" vs our "Female"). A
 * case-insensitive exact match is deliberately all we do: anything cleverer
 * would guess, and a wrong guess silently mis-records a resident's ethnicity.
 * No match → empty, and the resident picks for themselves.
 */
const matchOption = (v: unknown, options: readonly string[]): string => {
  const raw = str(v).trim();
  if (!raw) return "";
  return options.find((o) => o.toLowerCase() === raw.toLowerCase()) ?? "";
};

/**
 * `<input type="date">` renders blank unless the value is exactly YYYY-MM-DD,
 * so a date it cannot parse must become "" rather than silently disappearing.
 * Joy sends ISO, but tolerate DD/MM/YYYY the way the identity gate does.
 */
const toDateInputValue = (v: unknown): string => {
  const raw = str(v).trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const uk = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (uk) return `${uk[3]}-${uk[2].padStart(2, "0")}-${uk[1].padStart(2, "0")}`;
  return "";
};

/** Split a seeded UK number into dialling code + national part, matching how
 *  the form composes it on submit. Anything unrecognised stays in the number
 *  field with the default code, editable. */
const splitPhone = (v: unknown): { code: string; number: string } => {
  const raw = str(v).replace(/[\s()-]/g, "");
  if (!raw) return { code: "+44", number: "" };
  if (raw.startsWith("+44")) return { code: "+44", number: raw.slice(3) };
  if (raw.startsWith("44") && raw.length > 11) return { code: "+44", number: raw.slice(2) };
  if (raw.startsWith("0")) return { code: "+44", number: raw.slice(1) };
  return { code: "+44", number: raw };
};

// EMPLOYMENT_OPTIONS and PRIOR_PROGRAMMES are derived from the conversation
// config (config/conversation/llr_ire_v3.json) via `npm run gen:options`, so the
// form's labels can't drift from the chat. See scripts/gen-options.mjs. Other
// lists below are either form-only (no config counterpart) or deliberately
// worded differently for the form and stay hand-authored here.

// Primary health barrier — the LLM's health categories + an explicit "none",
// with friendly display labels. Value is what we store/transmit.
const HEALTH_BARRIER_OPTIONS = [
  { value: "mental_health", label: "Mental health", pa: "ਮਾਨਸਿਕ ਸਿਹਤ", gu: "માનસિક આરોગ્ય" },
  { value: "musculoskeletal", label: "Musculoskeletal (e.g. back, joints)", pa: "ਮਾਸਪੇਸ਼ੀ ਤੇ ਹੱਡੀਆਂ (ਜਿਵੇਂ ਪਿੱਠ, ਜੋੜ)", gu: "સ્નાયુ અને હાડકાં (દા.ત. પીઠ, સાંધા)" },
  { value: "long_term", label: "Long-term condition", pa: "ਲੰਮੇ ਸਮੇਂ ਦੀ ਬਿਮਾਰੀ", gu: "લાંબા ગાળાની બીમારી" },
  { value: "other", label: "Other", pa: "ਹੋਰ", gu: "અન્ય" },
  { value: "none", label: "None reported", pa: "ਕੋਈ ਨਹੀਂ ਦੱਸਿਆ ਗਿਆ", gu: "કોઈ નોંધાયું નથી" },
];

// (The age-bracket dropdown was replaced by an exact date of birth on the form —
// the conversation still collects a bracket, and that is what the analytical
// store keeps for MI. Yvett 14 Jul.)

const ETHNICITIES = [
  "White",
  "Asian or Asian British",
  "Black, Black British, Caribbean or African",
  "Mixed or Multiple ethnic groups",
  "Other ethnic group",
  "Prefer not to say",
];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer to self-describe", "Prefer not to say"];
const EDUCATION = [
  "No formal qualifications",
  "GCSE or equivalent",
  "A-level or equivalent",
  "Degree or higher",
  "Prefer not to say",
];
const REFERRAL_ROUTES = [
  "A health professional (GP, nurse or other)",
  "The Jobcentre or social prescriber",
  "A council or local support service",
  "Friend or family member",
  "Social media or online search",
];

// Country dialling codes for the phone field (Yvett 17 Jul; Naveen 17 Jul:
// European codes only). +44 (UK) is the default and Ireland sits next; the rest
// of Europe follows alphabetically. The backend accepts any +<code><digits>
// E.164-ish number, so extending the list is a one-line change.
const DIALLING_CODES = [
  { code: "+44", label: "+44 (UK)" },
  { code: "+353", label: "+353 (Ireland)" },
  { code: "+355", label: "+355 (Albania)" },
  { code: "+376", label: "+376 (Andorra)" },
  { code: "+43", label: "+43 (Austria)" },
  { code: "+375", label: "+375 (Belarus)" },
  { code: "+32", label: "+32 (Belgium)" },
  { code: "+387", label: "+387 (Bosnia & Herzegovina)" },
  { code: "+359", label: "+359 (Bulgaria)" },
  { code: "+385", label: "+385 (Croatia)" },
  { code: "+357", label: "+357 (Cyprus)" },
  { code: "+420", label: "+420 (Czechia)" },
  { code: "+45", label: "+45 (Denmark)" },
  { code: "+372", label: "+372 (Estonia)" },
  { code: "+358", label: "+358 (Finland)" },
  { code: "+33", label: "+33 (France)" },
  { code: "+49", label: "+49 (Germany)" },
  { code: "+30", label: "+30 (Greece)" },
  { code: "+36", label: "+36 (Hungary)" },
  { code: "+354", label: "+354 (Iceland)" },
  { code: "+39", label: "+39 (Italy)" },
  { code: "+383", label: "+383 (Kosovo)" },
  { code: "+371", label: "+371 (Latvia)" },
  { code: "+423", label: "+423 (Liechtenstein)" },
  { code: "+370", label: "+370 (Lithuania)" },
  { code: "+352", label: "+352 (Luxembourg)" },
  { code: "+356", label: "+356 (Malta)" },
  { code: "+373", label: "+373 (Moldova)" },
  { code: "+377", label: "+377 (Monaco)" },
  { code: "+382", label: "+382 (Montenegro)" },
  { code: "+31", label: "+31 (Netherlands)" },
  { code: "+389", label: "+389 (North Macedonia)" },
  { code: "+47", label: "+47 (Norway)" },
  { code: "+48", label: "+48 (Poland)" },
  { code: "+351", label: "+351 (Portugal)" },
  { code: "+40", label: "+40 (Romania)" },
  { code: "+378", label: "+378 (San Marino)" },
  { code: "+381", label: "+381 (Serbia)" },
  { code: "+421", label: "+421 (Slovakia)" },
  { code: "+386", label: "+386 (Slovenia)" },
  { code: "+34", label: "+34 (Spain)" },
  { code: "+46", label: "+46 (Sweden)" },
  { code: "+41", label: "+41 (Switzerland)" },
  { code: "+380", label: "+380 (Ukraine)" },
];

// --- Connect to Work only ---------------------------------------------------

const CONTACT_METHODS = [
  { value: "phone", label: "Phone", pa: "ਫ਼ੋਨ", gu: "ફોન" },
  { value: "email", label: "Email", pa: "ਈਮੇਲ", gu: "ઇમેલ" },
  { value: "sms", label: "SMS", pa: "SMS", gu: "SMS" },
];


// A pragmatic email check: one @, a dot-separated domain, no spaces. Deliberately
// not RFC 5322-exhaustive — that rejects valid addresses and accepts junk; this
// catches the real mistakes (missing @, no domain, trailing text).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

// UK phone: strip spaces, hyphens, parens. Accept 07xxxxxxxxx (11 digits),
// +447xxxxxxxxx / 447xxxxxxxxx (mobile), or a landline 0xxxxxxxxxx (10–11 digits).
// Also accept a bare 10-digit number with the leading 0 dropped (xxxxxxxxxx) —
// a very common way people write their number ("7594263218"). It is normalised
// to 0-form on submit (see normaliseUkPhone).
export function isValidUkPhone(value: string): boolean {
  const d = value.replace(/[\s()-]/g, "");
  if (/^\+?44\d{9,10}$/.test(d)) return true;      // +44 / 44 national form
  if (/^0\d{9,10}$/.test(d)) return true;          // 0-leading, 10–11 digits total
  if (/^\d{10}$/.test(d)) return true;             // bare 10 digits, leading 0 dropped
  return false;
}

/** Normalise an accepted UK phone number to a standard dialable form so the
 * coach always gets a consistent number. A bare 10-digit number (leading 0
 * dropped) is rewritten to 0-form (xxxxxxxxxx -> 0xxxxxxxxxx); 0-form and
 * +44/44 forms are returned unchanged (aside from stripping separators).
 * Assumes the value already passed isValidUkPhone. */
export function normaliseUkPhone(value: string): string {
  const d = value.replace(/[\s()-]/g, "");
  return /^\d{10}$/.test(d) ? `0${d}` : d;
}

/**
 * The subscriber part of a phone number, validated against its dialling code
 * (the number WITHOUT the code — the code comes from the dropdown).
 *
 * +44 keeps the lenient UK rules the form has always had: the resident may type
 * their number with or without the leading 0 ("07594263218" or "7594263218").
 * Other codes get a generic sanity check only — we can't meaningfully validate
 * every country's numbering plan, and guessing wrong would reject real numbers.
 */
export function isValidPhoneForCode(code: string, subscriber: string): boolean {
  const d = subscriber.replace(/[\s()-]/g, "");
  if (!/^\d+$/.test(d)) return false;
  if (code === "+44") {
    // 0-form (11 digits) or the 0 dropped (10 digits).
    return /^0\d{9,10}$/.test(d) || /^\d{10}$/.test(d);
  }
  return d.length >= 6 && d.length <= 14; // E.164: max 15 incl. country code
}

/**
 * Assemble the dialling code + subscriber number into one E.164-style string
 * for storage / transmission, so the coach always gets a dialable number.
 * A UK number's leading 0 is a national-dialling prefix and is dropped when the
 * +44 code is prepended (+44 07594… would not dial).
 */
export function composePhone(code: string, subscriber: string): string {
  let d = subscriber.replace(/[\s()-]/g, "");
  if (code === "+44") d = d.replace(/^0/, "");
  return `${code}${d}`;
}

/** Today as yyyy-mm-dd, for the date input's `max` (no future birthdays). */
export function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** DOB as the native date input holds it: ISO yyyy-mm-dd. The conversation
 * collects an age BRACKET (data minimisation while chatting), but the referral
 * itself needs the exact date for the coach / DWP, so it is asked here.
 *
 * Rejects impossible dates, the future, and anyone under 16 — the same floor
 * the eligibility engine enforces. 120 years is the upper sanity bound. */
export function isValidDob(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return false;
  const [, yyyy, mm, dd] = m;
  const year = Number(yyyy), month = Number(mm), day = Number(dd);
  const d = new Date(year, month - 1, day);
  // Round-trip check: catches 2000-02-31, which Date silently rolls forward.
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return false;

  const today = new Date();
  if (d > today) return false;
  let age = today.getFullYear() - year;
  const beforeBirthday =
    today.getMonth() < month - 1 ||
    (today.getMonth() === month - 1 && today.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 16 && age <= 120;
}

/** ISO (yyyy-mm-dd, what the control gives us) -> dd/mm/yyyy for the referral
 * payload: the coach and DWP read UK-format dates. */
export function dobToUk(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso.trim();
}

/** The conversation's multi-selects arrive as arrays; be tolerant of a single
 * string too. */
const strList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
};

/** Check/uncheck one value in a multi-select. "None" is exclusive: ticking it
 * clears the rest, and ticking anything else clears "None". */
const toggle = (list: string[], value: string, checked: boolean): string[] => {
  if (!checked) return list.filter((v) => v !== value);
  if (value === "none") return ["none"];
  return [...list.filter((v) => v !== "none"), value];
};

/**
 * Referral details form (Yvett 09 Jun): all fields at once, auto-populated from
 * the conversation.
 *
 * TWO VARIANTS, one component, branching on `service` (Yvett 14 Jul):
 *  - WorkWell: health-led — primary health barrier + reason for referral, and
 *    the GP picker also offers Northamptonshire practices (ww_eligible covers
 *    NORTHANTS, c2w_eligible does not).
 *  - Connect to Work: employment-led — no health barrier; adds preferred
 *    contact method, disadvantaged-group details and prior DWP programme (both
 *    prefilled from the conversation); email is optional; GP picker is LLR-only.
 *
 * DOB is asked on BOTH forms. The conversation deliberately collects only an
 * age BRACKET (data minimisation while chatting), but the referral going to a
 * coach / DWP needs the exact date, so the resident types it here.
 */
export function ReferralForm({ service, collectedData: cd, onSubmit, onClose }: ReferralFormProps) {
  const { t, lang } = useI18n();
  const isC2W = service !== "WorkWell";
  const name = isC2W ? "Connect to Work" : "WorkWell";
  const programName = isC2W ? "Connect to Work LLR" : "WorkWell LLR";
  const reason0 =
    str(cd.referral_summary) ||
    str(cd.coach_summary) ||
    (cd.service_recommended ? `Matched to ${name}` : "");
  // Prefilled-but-EDITABLE fields — seed from the conversation, let the user
  // correct them. `none`/"" health means "None reported".
  const [firstName, setFirstName] = useState(str(cd.first_name));
  const [lastName, setLastName] = useState(str(cd.last_name));
  // Seeded from the conversation where it exists. For a GP referral that is
  // what Joy sent, so the resident is not retyping details their GP supplied;
  // for a self-referral these are simply absent and the field starts empty.
  // Everything stays EDITABLE — the record may be stale and the resident is
  // the authority on their own details.
  const [dob, setDob] = useState(() => toDateInputValue(cd.date_of_birth));
  const [postcode, setPostcode] = useState(str(cd.postcode));
  const [employment, setEmployment] = useState(str(cd.employment_status));
  const [healthBarrier, setHealthBarrier] = useState(
    str(cd.health_condition_category).trim() || "none",
  );
  const [reason, setReason] = useState(reason0);

  // Address, split for cleaner entry. Line 1 is the only genuinely-empty
  // required part; town is pre-filled from postcodes.io as a convenience (see
  // the lookup effect below) and stays fully editable. Everything combines
  // into a single `address` string on submit — the payload shape is unchanged.
  const [addressLine1, setAddressLine1] = useState(str(cd.address));
  const [addressLine2, setAddressLine2] = useState("");
  const [town, setTown] = useState(str(cd.city));
  // Postcode comes pre-filled from the conversation; show it read-only with a
  // "Change" escape hatch so a conversation typo can still be fixed here.
  const [postcodeEditable, setPostcodeEditable] = useState(!str(cd.postcode).trim());
  const [gpSurgery, setGpSurgery] = useState(str(cd.gp_surgery));
  // Northants practices only on the WorkWell form (see useGpPractices).
  const gpPractices = useGpPractices(!isC2W);
  const [email, setEmail] = useState(str(cd.email));
  const seededPhone = splitPhone(cd.phone);
  const [phone, setPhone] = useState(seededPhone.number);
  // Dialling code for the phone field, defaulting to the UK (Yvett 17 Jul).
  const [phoneCode, setPhoneCode] = useState(seededPhone.code);
  // Closed vocabularies: seeded only on an exact (case-insensitive) match, so
  // an unfamiliar upstream value leaves the resident to choose rather than
  // recording a guess.
  const [gender, setGender] = useState(() => matchGender(cd.gender));
  const [ethnicity, setEthnicity] = useState(() => matchEthnicity(cd.ethnicity));
  const [education, setEducation] = useState(() => matchOption(cd.education, EDUCATION));
  // Connect to Work only, prefilled from the conversation.
  const [contactMethod, setContactMethod] = useState("");
  const [groups, setGroups] = useState<string[]>(() => strList(cd.disadvantaged_groups));
  const [programmes, setProgrammes] = useState<string[]>(() => strList(cd.prior_programmes));
  const [route, setRoute] = useState("");
  const [routeOther, setRouteOther] = useState("");
  // Set of field keys that failed validation on the last Continue attempt.
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const err = (k: string) => errors.has(k);

  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(dialogRef, onClose);

  // Auto-size the reason textarea to fit its content (seeded from the AI summary,
  // then editable — re-run as the user types).
  useLayoutEffect(() => {
    const el = reasonRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [reason]);

  // Pre-fill the town from postcodes.io as a convenience. Best-effort ONLY:
  // postcodes.io is a free public API with no SLA and the postcode may be a
  // conversation typo, so any failure (network, 404, bad JSON) leaves the town
  // empty for the resident to type. Re-runs when the postcode is edited. Never
  // overwrites a town the resident has already typed. `admin_district` is a
  // council area, not always the Royal Mail post town — hence pre-fill only,
  // always editable.
  useEffect(() => {
    const pc = postcode.trim();
    if (!pc) return;
    let cancelled = false;
    fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body?.result) return;
        const district = str(body.result.admin_district).trim();
        // Only pre-fill an empty town — never clobber what the resident typed.
        if (district) setTown((cur) => (cur.trim() ? cur : district));
      })
      .catch(() => {
        /* fail soft — resident types the town */
      });
    return () => {
      cancelled = true;
    };
    // Re-run when the postcode changes (e.g. after "Change").
  }, [postcode]);

  const submit = () => {
    // Validate every field the user can edit — required before we advance.
    const next = new Set<string>();
    if (!firstName.trim()) next.add("firstName");
    if (!lastName.trim()) next.add("lastName");
    if (!dob.trim()) next.add("dob");
    else if (!isValidDob(dob.trim())) next.add("dobFormat");
    if (!postcode.trim()) next.add("postcode");
    if (!employment) next.add("employment");
    // Address line 1 and town are required; line 2 is optional. Non-empty only
    // — a format regex rejects valid UK addresses (flats, named houses, BFPO).
    if (!addressLine1.trim()) next.add("addressLine1");
    if (!town.trim()) next.add("town");
    if (!gpSurgery.trim()) next.add("gpSurgery");
    // Email is optional on Connect to Work, required on WorkWell. When given,
    // it must be well-formed either way.
    if (!isC2W && !email.trim()) next.add("email");
    if (email.trim() && !isValidEmail(email.trim())) next.add("emailFormat");
    if (!phone.trim()) next.add("phone");
    else if (!isValidPhoneForCode(phoneCode, phone.trim())) next.add("phoneFormat");
    if (!gender) next.add("gender");
    if (!ethnicity) next.add("ethnicity");
    if (!route) next.add("route");
    if (route === "Other" && !routeOther.trim()) next.add("routeOther");

    // Asked on BOTH forms (Yvett 17 Jul — was Connect to Work only): a coach
    // needs to know how to reach the resident whichever programme they go to.
    if (!contactMethod) next.add("contactMethod");

    if (isC2W) {
      // Employment-led: the two prefilled multi-selects.
      if (groups.length === 0) next.add("groups");
      if (programmes.length === 0) next.add("programmes");
    } else {
      // Health-led.
      if (!healthBarrier) next.add("healthBarrier");
      if (!reason.trim()) next.add("reason");
      if (!education) next.add("education");
    }

    if (next.size > 0) {
      setErrors(next);
      // Move focus to the first field with an error for keyboard/screen-reader
      // users. Deferred: aria-invalid is set by the re-render that this
      // setErrors triggers, so querying synchronously found nothing and focus
      // never moved (Sept 2026 accessibility sweep).
      requestAnimationFrame(() => {
        const first = dialogRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
        first?.focus();
      });
      return;
    }
    setErrors(new Set());

    const referralRoute = route === "Other" ? routeOther.trim() : route;
    // Common to both programmes. `age_bracket` still rides along from the
    // conversation: it is what the ANALYTICAL store keeps for MI, while the
    // exact `date_of_birth` is a direct identifier and stays PII-side.
    const common = {
      program_name: programName,
      full_name: [firstName.trim(), lastName.trim()].filter(Boolean).join(" "),
      date_of_birth: dobToUk(dob),   // dd/mm/yyyy for the coach / DWP
      age_bracket: str(cd.age_bracket),
      postcode: postcode.trim(),
      // Combine the four entered parts into the single stored address string —
      // the payload / PII store / transmission shape is unchanged.
      address: [addressLine1, addressLine2, town, postcode]
        .map((p) => p.trim())
        .filter(Boolean)
        .join(", "),
      gp_surgery: gpSurgery.trim(),
      email: email.trim(),
      // Code + number, composed E.164-style so the coach always gets a
      // dialable number whichever country it is (Yvett 17 Jul).
      phone: composePhone(phoneCode, phone.trim()),
      gender,
      ethnicity,
      employment_status: employment,
      referral_route: referralRoute,
      // Both programmes (Yvett 17 Jul — was C2W only).
      preferred_contact_method: contactMethod,
    };

    (window as unknown as { _referralForm?: unknown })._referralForm = isC2W
      ? {
          ...common,
          disadvantaged_groups: groups,
          prior_programmes: programmes,
        }
      : {
          ...common,
          primary_health_barrier: healthBarrier === "none" ? "None reported" : healthBarrier,
          reason_for_referral: reason.trim(),
          education,
        };
    onSubmit();
  };

  return (
    <div
      className="form-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={dialogRef} className="referral-form" role="dialog" aria-modal="true" aria-label={t("form.dialogLabel")}>
        <h2>{t("form.title")}</h2>
        <p className="form-sub">{t("form.intro").replace("{service}", name)}</p>

        <div className="form-grid">
          <label>
            {t("form.programName")}
            <input type="text" value={programName} readOnly />
          </label>
          <label className={err("firstName") ? "has-error" : ""}>
            {t("form.firstName")}
            <input
              type="text"
              autoComplete="given-name"
              value={firstName}
              aria-invalid={err("firstName")}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {err("firstName") && <span className="field-error">{t("form.error.firstName")}</span>}
          </label>
          <label className={err("lastName") ? "has-error" : ""}>
            {t("form.lastName")}
            <input
              type="text"
              autoComplete="family-name"
              value={lastName}
              aria-invalid={err("lastName")}
              onChange={(e) => setLastName(e.target.value)}
            />
            {err("lastName") && <span className="field-error">{t("form.error.lastName")}</span>}
          </label>
          <label className={err("dob") || err("dobFormat") ? "has-error" : ""}>
            {t("form.dob")}
            {/* Native date input: gives the calendar picker, and still lets the
                resident type the date straight in. Held as ISO (yyyy-mm-dd) —
                what the control uses — and converted to dd/mm/yyyy on submit. */}
            <input
              type="date"
              autoComplete="bday"
              max={todayIso()}
              value={dob}
              aria-invalid={err("dob") || err("dobFormat")}
              onChange={(e) => setDob(e.target.value)}
            />
            {err("dob") && <span className="field-error">{t("form.error.dob")}</span>}
            {err("dobFormat") && (
              <span className="field-error">
                Please enter a valid date of birth. You must be 16 or over to use this service.
              </span>
            )}
          </label>
          <label className={"form-full" + (err("addressLine1") ? " has-error" : "")}>
            {t("form.addressLine1")}
            <input
              type="text"
              autoComplete="address-line1"
              placeholder={t("form.placeholder.addressLine1")}
              value={addressLine1}
              aria-invalid={err("addressLine1")}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
            {err("addressLine1") && (
              <span className="field-error">{t("form.error.addressLine1")}</span>
            )}
          </label>
          <label className="form-full">
            {t("form.addressLine2")} <span className="field-optional">{t("form.optional")}</span>
            <input
              type="text"
              autoComplete="address-line2"
              placeholder={t("form.placeholder.addressLine2")}
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
          </label>
          <label className={err("town") ? "has-error" : ""}>
            {t("form.town")}
            <input
              type="text"
              autoComplete="address-level2"
              value={town}
              aria-invalid={err("town")}
              onChange={(e) => setTown(e.target.value)}
            />
            {err("town") && <span className="field-error">{t("form.error.town")}</span>}
          </label>
          <label className={err("postcode") ? "has-error" : ""}>
            {/* The "Change" button sits OUTSIDE the label text (in the
                label-row wrapper) so it isn't folded into the input's
                accessible name; it carries its own aria-label. Escape hatch:
                the postcode is pre-filled from the conversation, so if it was
                a typo "Change" unlocks it (and re-runs the town lookup). */}
            <span className="field-label-row">
              <span>{t("form.postcode")}</span>
              {!postcodeEditable && (
                <button
                  type="button"
                  className="field-inline-link"
                  aria-label={t("form.changePostcode")}
                  onClick={() => setPostcodeEditable(true)}
                >
                  {t("form.change")}
                </button>
              )}
            </span>
            <input
              type="text"
              autoComplete="postal-code"
              value={postcode}
              readOnly={!postcodeEditable}
              aria-invalid={err("postcode")}
              onChange={(e) => setPostcode(e.target.value)}
            />
            {err("postcode") && <span className="field-error">{t("form.error.postcode")}</span>}
          </label>
          <label className={"form-full" + (err("gpSurgery") ? " has-error" : "")}>
            {t("form.gpSurgery")}
            <input
              type="text"
              list="gp-practice-list"
              autoComplete="off"
              placeholder={t("form.placeholder.gpSurgery")}
              value={gpSurgery}
              aria-invalid={err("gpSurgery")}
              onChange={(e) => setGpSurgery(e.target.value)}
            />
            <datalist id="gp-practice-list">
              {gpPractices.map((p) => (
                <option key={p.ods_code} value={p.name}>
                  {p.postcode}
                </option>
              ))}
            </datalist>
            {err("gpSurgery") && (
              <span className="field-error">{t("form.error.gpSurgery")}</span>
            )}
          </label>
          <label className={err("email") || err("emailFormat") ? "has-error" : ""}>
            {isC2W ? `${t("form.email")} ${t("form.optional")}` : t("form.email")}
            <input
              type="email"
              autoComplete="email"
              placeholder={t("form.placeholder.email")}
              value={email}
              aria-invalid={err("email") || err("emailFormat")}
              onChange={(e) => setEmail(e.target.value)}
            />
            {err("email") && <span className="field-error">{t("form.error.email")}</span>}
            {err("emailFormat") && <span className="field-error">{t("form.error.emailFormat")}</span>}
          </label>
          <label className={"phone-label" + (err("phone") || err("phoneFormat") ? " has-error" : "")}>
            {t("form.phone")}
            {/* Dialling code + number (Yvett 17 Jul). The code is its own control
                so the resident never has to guess whether to type 0 or +44; the
                two are composed E.164-style on submit. */}
            <span className="phone-field">
              <select
                className="phone-field__code"
                aria-label={t("form.diallingCode")}
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
              >
                {DIALLING_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                className="phone-field__number"
                type="tel"
                autoComplete="tel-national"
                placeholder={t("form.placeholder.phone")}
                value={phone}
                aria-invalid={err("phone") || err("phoneFormat")}
                onChange={(e) => setPhone(e.target.value)}
              />
            </span>
            {err("phone") && <span className="field-error">{t("form.error.phone")}</span>}
            {err("phoneFormat") && (
              <span className="field-error">
                {phoneCode === "+44"
                  ? "Please enter a valid UK phone number, e.g. 07700 900123."
                  : "Please enter a valid phone number (digits only, without the country code)."}
              </span>
            )}
          </label>
          <label className={err("gender") ? "has-error" : ""}>
            {t("form.gender")}
            <select value={gender} aria-invalid={err("gender")} onChange={(e) => setGender(e.target.value)}>
              <option value="">{t("form.select")}</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>{t(`option.gender.${g}`)}</option>
              ))}
            </select>
            {err("gender") && <span className="field-error">{t("form.error.select")}</span>}
          </label>
          <label className={err("ethnicity") ? "has-error" : ""}>
            {t("form.ethnicity")}
            <select value={ethnicity} aria-invalid={err("ethnicity")} onChange={(e) => setEthnicity(e.target.value)}>
              <option value="">{t("form.select")}</option>
              {ETHNICITIES.map((x) => (
                <option key={x} value={x}>{t(`option.ethnicity.${x}`)}</option>
              ))}
            </select>
            {err("ethnicity") && <span className="field-error">{t("form.error.select")}</span>}
          </label>
          {!isC2W && (
            <label className={err("education") ? "has-error" : ""}>
              {t("form.education")}
              <select value={education} aria-invalid={err("education")} onChange={(e) => setEducation(e.target.value)}>
                <option value="">{t("form.select")}</option>
                {EDUCATION.map((x) => (
                  <option key={x} value={x}>{t(`option.education.${x}`)}</option>
                ))}
              </select>
              {err("education") && <span className="field-error">{t("form.error.select")}</span>}
            </label>
          )}
          {/* Asked on BOTH forms (Yvett 17 Jul — was Connect to Work only). */}
          <label className={err("contactMethod") ? "has-error" : ""}>
            {t("form.contactMethod")}
            <select
              value={contactMethod}
              aria-invalid={err("contactMethod")}
              onChange={(e) => setContactMethod(e.target.value)}
            >
              <option value="">{t("form.select")}</option>
              {CONTACT_METHODS.map((o) => (
                <option key={o.value} value={o.value}>{optionLabel(o, lang)}</option>
              ))}
            </select>
            {err("contactMethod") && <span className="field-error">{t("form.error.select")}</span>}
          </label>
          <label className={err("employment") ? "has-error" : ""}>
            {t("form.employment")}
            <select value={employment} aria-invalid={err("employment")} onChange={(e) => setEmployment(e.target.value)}>
              <option value="">{t("form.select")}</option>
              {EMPLOYMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{optionLabel(o, lang)}</option>
              ))}
            </select>
            {err("employment") && <span className="field-error">{t("form.error.select")}</span>}
          </label>
          {!isC2W && (
            <label className={err("healthBarrier") ? "has-error" : ""}>
              {t("form.healthBarrier")}
              <select value={healthBarrier} aria-invalid={err("healthBarrier")} onChange={(e) => setHealthBarrier(e.target.value)}>
                <option value="">{t("form.select")}</option>
                {HEALTH_BARRIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{optionLabel(o, lang)}</option>
                ))}
              </select>
              {err("healthBarrier") && <span className="field-error">{t("form.error.select")}</span>}
            </label>
          )}
          {isC2W && (
            <>
              <fieldset className={"form-full check-group" + (err("groups") ? " has-error" : "")}>
                <legend>{t("form.circumstances")}</legend>
                {DISADVANTAGED_GROUPS.map((o) => (
                  <label key={o.value} className="check-item">
                    <input
                      type="checkbox"
                      checked={groups.includes(o.value)}
                      onChange={(e) => setGroups(toggle(groups, o.value, e.target.checked))}
                    />
                    {optionLabel(o, lang)}
                  </label>
                ))}
                {err("groups") && (
                  <span className="field-error">
                    {t("form.error.circumstances")}
                  </span>
                )}
              </fieldset>
              <fieldset className={"form-full check-group" + (err("programmes") ? " has-error" : "")}>
                <legend>{t("form.priorProgrammes")}</legend>
                {PRIOR_PROGRAMMES.map((o) => (
                  <label key={o.value} className="check-item">
                    <input
                      type="checkbox"
                      checked={programmes.includes(o.value)}
                      onChange={(e) => setProgrammes(toggle(programmes, o.value, e.target.checked))}
                    />
                    {optionLabel(o, lang)}
                  </label>
                ))}
                {err("programmes") && (
                  <span className="field-error">
                    {t("form.error.priorProgrammes")}
                  </span>
                )}
              </fieldset>
            </>
          )}
          <label className={"form-full" + (err("route") ? " has-error" : "")}>
            {t("form.route")}
            <select value={route} aria-invalid={err("route")} onChange={(e) => setRoute(e.target.value)}>
              <option value="">{t("form.select")}</option>
              {REFERRAL_ROUTES.map((r) => (
                <option key={r} value={r}>{t(`option.route.${r}`)}</option>
              ))}
              <option value="Other">{t("option.route.Other")}</option>
            </select>
            {err("route") && <span className="field-error">{t("form.error.select")}</span>}
          </label>
          {route === "Other" && (
            <label className={"form-full" + (err("routeOther") ? " has-error" : "")}>
              {t("form.routeOther")}
              <input
                type="text"
                placeholder={t("form.placeholder.routeOther")}
                value={routeOther}
                aria-invalid={err("routeOther")}
                onChange={(e) => setRouteOther(e.target.value)}
              />
              {err("routeOther") && <span className="field-error">{t("form.error.routeOther")}</span>}
            </label>
          )}
          {!isC2W && (
            <label className={"form-full" + (err("reason") ? " has-error" : "")}>
              {t("form.reason")}
              <textarea
                ref={reasonRef}
                rows={2}
                value={reason}
                aria-invalid={err("reason")}
                onChange={(e) => setReason(e.target.value)}
              />
              {err("reason") && <span className="field-error">{t("form.error.reason")}</span>}
            </label>
          )}
        </div>

        <button type="button" className="form-submit-btn" onClick={submit}>
          {t("form.continue")}
        </button>
      </div>
    </div>
  );
}
